import { assign, each, forEach, get, isArray, merge, remove } from 'lodash';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpCancelService } from '../http.cancel/http.cancel.service';
import { LoggerService } from '../logger/logger.service';
import { IBatchActionState, TimeMode, INavBar, IFilterData } from './types';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root'
})
export class InputDatastoreService {
  private readonly httpCancelService: HttpCancelService;
  private log: LoggerService;
  private readonly inputsSubject: BehaviorSubject<Object>;
  private readonly widgetQueue: Array<Function>;
  private readonly filterQueue: Array<Function>;
  private requestId: number;
  private batchActionState: IBatchActionState;

  timeMode: BehaviorSubject<TimeMode>;
  inputs: Object;
  outputs: Object;
  outputsSubject: BehaviorSubject<Object>;
  combinedSubjects: Observable<Object>;
  navigationData: INavBar | Object = {};
  sideBarData: Object;
  filters: BehaviorSubject<Array<any>>;
  isEditMonitorVisible: BehaviorSubject<boolean>;
  urlWhitelist: Array<string>;

  constructor(
    httpCancelService: HttpCancelService,
    log: LoggerService,
    private readonly userService: UserService
  ) {
    this.httpCancelService = httpCancelService;
    this.log = log;

    // Time modes: real-time and historical
    // Based on this parameter we will be switching entities data objects
    this.timeMode = new BehaviorSubject<TimeMode>('real-time');

    this.inputs = {};
    this.inputsSubject = new BehaviorSubject({});

    this.outputs = {};
    this.outputsSubject = new BehaviorSubject({});

    this.combinedSubjects = combineLatest(this.inputsSubject, this.outputsSubject);

    this.sideBarData = {};
    this.filters = new BehaviorSubject([]);

    this.widgetQueue = [];
    this.filterQueue = [];
    this.isEditMonitorVisible = new BehaviorSubject(false);

    this.requestId = 0;
    this.batchActionState = null;
    this.urlWhitelist = httpCancelService.urlWhitelist;
  }

  isDarkTheme(): boolean {
    return this.userService.getDefaultTheme() === 'dark';
  }

  getTimeMode(): TimeMode {
    return get(this.timeMode, 'value', '') as TimeMode;
  }

  setTimeMode(mode: TimeMode): void {
    this.timeMode.next(mode);
  }

  resetTimeMode(): void {
    this.timeMode.next('real-time');
  }

  getInputs(): Object {
    return this.inputs;
  }

  getOneInput(key: string): any {
    if (key) {
      return this.inputs[key];
    }
    return null;
  }

  setFilters(filterData: Array<IFilterData>): void {
    if (isArray(filterData)) {
      each(filterData, filter => {
        if (this.outputs[filter.id]) {
          filter.def.options.defaultValues = this.outputs[filter.id];
        }
      });

      this.filters.next(filterData);
    }
  }

  setInput(key: string, value: any): void {
    this.inputs[key] = value;
  }

  setInputsGlobally(obj: Object, broadcast = true, ignoreCancellation = false): void {
    this.requestId++;
    this.log.debug(`(input) Req. seq no. -  ${this.requestId}, obj: ${JSON.stringify(obj)}`);

    assign(this.inputs, obj);
    this.inputsSubject.next(this.inputs);

    if (broadcast) {
      this.broadcastUpdate(this.getInputs(), true, ignoreCancellation);
    }
  }

  getOutputs(): Object {
    return this.outputs;
  }

  setOutputs(obj: Object, broadcast = true, ignoreCancellation = false): void {
    this.requestId++;
    this.log.debug(`(output) Req. seq no. -  ${this.requestId}, obj: ${JSON.stringify(obj)}`);
    assign(this.outputs, obj);
    this.outputsSubject.next(this.outputs);

    if (broadcast) {
      this.broadcastUpdate(this.getOutputs(), true, ignoreCancellation);
    }
  }

  addNavigation(navBar: INavBar): void {
    merge(this.navigationData, navBar);
  }

  addSideBar(obj: Object): void {
    merge(this.sideBarData, obj);
  }

  broadcastUpdate(inputs: Object, ignoreDupRequests = true, ignoreCancellation = false): void {
    if (!ignoreCancellation) {
      this.cancelAllRequests();
    }

    this.filterToPromise(inputs);
    this.widgetToPromise(inputs, ignoreDupRequests);
  }

  registerWidget(fn: Function): void {
    this.registerWith(this.widgetQueue, fn);
  }

  registerFilters(fn: Function): void {
    this.registerWith(this.filterQueue, fn);
  }

  registerWith(list: Array<Function>, fn: Function): void {
    if (fn) {
      list.push(fn);
      return;
    }
    this.log.debug('No callback function supplied for registration');
  }

  clearFilters(): void {
    this.filters.next([]);
    this.inputsSubject.next({});
    this.outputsSubject.next({});
  }

  clearWidget(fn: Function): void {
    remove(this.widgetQueue, item => {
      return item === fn;
    });
  }

  clearWidgetQueue(): void {
    this.widgetQueue.length = 0;
  }

  clearInput(key: string): void {
    if (this.inputs.hasOwnProperty(key)) {
      delete this.inputs[key];
    }
  }

  clearInputs(): void {
    for (const property in this.inputs) {
      if (this.inputs.hasOwnProperty(property)) {
        delete this.inputs[property];
      }
    }
  }

  clearOutputs(): void {
    for (const property in this.outputs) {
      if (this.outputs.hasOwnProperty(property)) {
        delete this.outputs[property];
      }
    }
  }

  clearQueues(): void {
    each([this.filterQueue, this.widgetQueue], queue => (queue.length = 0));
    this.clearInputs();
    this.clearOutputs();
    this.clearFilters();
  }

  cancelAllRequests(): void {
    this.httpCancelService.cancelPendingRequests();
  }

  private widgetToPromise(inputs: Object, ignoreDupRequests: boolean): void {
    forEach(this.widgetQueue, fn => fn(inputs, ignoreDupRequests));
  }

  private filterToPromise(inputs: Object): void {
    forEach(this.filterQueue, fn => fn(inputs));
  }

  clearInputDataStore(): void {
    this.httpCancelService.cancelPendingRequests();
    this.clearQueues();
  }

  getBatchActionState(): IBatchActionState {
    return this.batchActionState;
  }

  setBatchActionState(value: IBatchActionState): void {
    this.batchActionState = value;
  }
}
